import React, { useState } from 'react';
import { useSoftKeyboardField } from '../../hooks/useSoftKeyboardField';

interface FireModeModalProps {
  isActive: boolean;
  orderAction: 'pause' | 'cancel' | 'hold';
  onActivate: () => void;
  onDeactivate: () => void;
  onCancel: () => void;
}

const FireModeModal: React.FC<FireModeModalProps> = ({
  isActive,
  orderAction,
  onActivate,
  onDeactivate,
  onCancel,
}) => {
  const [confirmText, setConfirmText] = useState('');
  const confirmKeyboard = useSoftKeyboardField({
    id: 'fire-mode-confirm',
    type: 'alphanumeric',
    label: 'Type CONFIRM',
    value: confirmText,
    onChange: setConfirmText,
  });

  const actionDescriptions = {
    pause: 'Pause all active orders (can be resumed after evacuation)',
    cancel: 'Cancel all active orders with customer notification',
    hold: 'Hold all active orders for later processing',
  };

  const canDeactivate = confirmText.toLowerCase() === 'confirm';

  if (!isActive) {
    // Activation modal
    return (
      <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex justify-center items-center p-4 overflow-y-auto">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] flex flex-col overflow-hidden my-auto">
          <header className="p-4 border-b bg-red-50 flex-shrink-0">
            <h2 className="text-xl font-bold text-red-800">Activate Fire Evacuation Mode</h2>
          </header>
          <main className="p-4 flex-1 min-h-0 overflow-y-auto">
            <div className="mb-4">
              <p className="text-sm text-gray-700 mb-3">
                Activating fire evacuation mode will:
              </p>
              <ul className="list-disc list-inside text-sm text-gray-600 space-y-1 mb-3">
                <li>{actionDescriptions[orderAction]}</li>
                <li>Queue all new incoming orders (delivery/online)</li>
                <li>Prevent new order placement</li>
                <li>Display fire mode banner throughout the system</li>
              </ul>
            </div>

            <div className="p-3 bg-red-50 border border-red-200 rounded">
              <p className="text-sm text-red-800 font-semibold">
                ⚠️ This is for emergency situations only (fire evacuation or fire drill)
              </p>
            </div>
          </main>
          <footer className="p-4 border-t flex justify-end gap-2 flex-shrink-0">
            <button
              onClick={onCancel}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={onActivate}
              className="px-4 py-2 text-white bg-red-500 rounded hover:bg-red-600 transition-colors"
            >
              Activate Fire Mode
            </button>
          </footer>
        </div>
      </div>
    );
  }

  // Deactivation modal
  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex justify-center items-center p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] flex flex-col overflow-hidden my-auto">
        <header className="p-4 border-b bg-green-50 flex-shrink-0">
          <h2 className="text-xl font-bold text-green-800">Deactivate Fire Evacuation Mode</h2>
        </header>
        <main className="p-4 flex-1 min-h-0 overflow-y-auto">
          <div className="mb-4">
            <p className="text-sm text-gray-700 mb-3">
              Deactivating fire mode will:
            </p>
            <ul className="list-disc list-inside text-sm text-gray-600 space-y-1 mb-3">
              <li>Resume processing of queued orders</li>
              <li>Allow new order placement</li>
              <li>Process orders based on configured action ({orderAction})</li>
            </ul>
          </div>

          <div className="mb-4">
            <p className="text-sm font-semibold text-gray-700 mb-2">
              Type "CONFIRM" to deactivate:
            </p>
            <input
              type="text"
              value={confirmText}
              placeholder="Type CONFIRM here"
              className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-green-500 focus:border-green-500"
              data-soft-keyboard-handled
              {...confirmKeyboard}
            />
          </div>
        </main>
        <footer className="p-4 border-t flex justify-end gap-2 flex-shrink-0">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onDeactivate}
            disabled={!canDeactivate}
            className={`px-4 py-2 text-white rounded transition-colors ${
              canDeactivate
                ? 'bg-green-500 hover:bg-green-600'
                : 'bg-gray-300 cursor-not-allowed'
            }`}
          >
            Deactivate Fire Mode
          </button>
        </footer>
      </div>
    </div>
  );
};

export default FireModeModal;

